import { PrismicRichText } from '@prismicio/react';
import Image from 'next/image';
import ContentContainer from 'remote-com-shared/components/ContentContainer';
import { fieldHasContent } from 'remote-com-shared/domains/prismic/helpers';
import { fluidProp, whenProp } from 'remote-com-shared/style/helpers';
import { AnimatedSection } from 'remote-com-shared/style/motion';
import styled, { css } from 'styled-components';

import { QuestionsAndConsiderations } from './FAQSection';
import { OverviewSectionFormButton } from './OverviewSectionModalForm';
import Heart from './assets/background-heart-icon.png';
import Users from './assets/background-users-icon.png';

import { LinkButton } from '@/src/components/Button';
import { defaultPrismicComponents } from '@/src/domains/prismic/render';
import {
  desktopBreakpoint,
  fromTabletBreakpoint,
  tabletBreakpoint,
  mobileBreakpoint,
  fromDesktopBreakpoint,
} from '@/src/domains/shared/breakpoints';
import { defaultAnimation } from '@/src/style/motion';
import { ComingSoonWrapper } from '@/src/views/IntegrationsForCustomers/PartnersSection';

const defaultCTAProps = {
  href: `${process.env.NEXT_PUBLIC_EMPLOY_BASE_URL}/sign-up`,
  rel: 'nofollow',
};

export const Wrapper = styled.article`
  position: relative;

  ${tabletBreakpoint(css`
    display: flex;
    ${({ $isEven }) => {
      switch ($isEven) {
        case true:
        default:
          return css`
            flex-direction: column-reverse;
          `;
      }
    }}
  `)}

  ${({ $wideContent }) => {
    switch ($wideContent) {
      case false:
        return fluidProp(['padding-top', 'padding-bottom'], '40px', '50px');
      case true:
      default:
        return fluidProp(['padding-top', 'padding-bottom'], '40px', '30px');
    }
  }}

  &:nth-of-type(1) {
    padding-top: 0;
  }

  ${fluidProp(['margin-top', 'margin-bottom'], '10px', '100px')}

  ${whenProp(
    '$onEdge',
    css`
      ${fluidProp(['margin-top', 'margin-bottom'], '10px', '180px')}

      &:not(:only-child):nth-of-type(1) {
        ${fluidProp('margin-top', '10px', '100px')}
      }
    `
  )}

  ${defaultAnimation}
`;

export const WrapperSingleBlock = styled(Wrapper)`
  margin-top: 0;
  margin-bottom: 0;
  padding-bottom: 0;
  padding-top: 0;
`;

export const ImageWrapper = styled.div`
  display: flex;
  flex-basis: 46%;
  max-width: 800px;

  ${tabletBreakpoint(css`
    margin-left: 50px;
    margin-right: 50px;
  `)}

  ${({ $bigImage }) => {
    switch ($bigImage) {
      case true:
        return css`
          display: none;

          ${tabletBreakpoint(css`
            display: block;
          `)}
        `;
      case false:
      default:
        return css`
          display: block;
        `;
    }
  }}
`;

export const BigImageWrapper = styled.div`
  display: none;

  ${fromTabletBreakpoint(css`
    display: block;
    position: absolute;
    top: 0;

    ${({ $isEven }) => {
      switch ($isEven) {
        case false:
          return css`
            right: 55%;
          `;
        case true:
        default:
          return css`
            left: 55%;
          `;
      }
    }}
  `)}

  ${fromDesktopBreakpoint(css`
    top: -15%;
  `)}
`;

export const BigImageContainer = styled.div`
  ${({ $onEdge }) => {
    switch ($onEdge) {
      case true:
        return css``;
      case false:
      default:
        return css`
          ${fluidProp(['padding-top', 'padding-bottom'], '40px', '90px')}
        `;
    }
  }}

  ${tabletBreakpoint(css`
    display: none;
  `)}
`;

export const ContentWrapper = styled.div`
  flex-basis: 50%;
`;

export const SingleContentWrapper = styled(ContentWrapper)`
  ${fromTabletBreakpoint(
    css`
      padding-top: 130px;
    `
  )}
`;

export const SectionContentContainer = styled(ContentContainer)`
  display: flex;
  /* show illustration before the content */
  flex-direction: column-reverse;
  scroll-margin-top: 100px;

  ${({ $withFAQ }) => {
    switch ($withFAQ) {
      case true:
        return css`
          align-items: flex-start;
        `;
      case false:
      default:
        return css`
          align-items: center;
        `;
    }
  }}

  > * + * {
    ${fluidProp('margin-bottom', '20px', '80px')}
  }

  ${tabletBreakpoint(
    whenProp('$wideContent', ({ $isEven }) => {
      switch ($isEven) {
        case false:
          return css`
            align-items: start;
            ${ImageWrapper} {
              ${fluidProp('margin-left', '-16px', '-48px')}
              max-width: 85%;
            }
          `;
        case true:
        default:
          return css`
            align-items: end;
            ${ImageWrapper} {
              ${fluidProp('margin-right', '-16px', '-48px')}
              max-width: 85%;
            }
          `;
      }
    })
  )}

  ${fromTabletBreakpoint(css`
    > * + * {
      margin-top: 0;
    }

    ${whenProp(
      '$onEdge',
      css`
        min-height: 400px;
      `
    )}

    ${whenProp(
      '$wideContent',
      css`
        margin: 0;
        padding: 0;
        max-width: unset;
        align-items: center;

        ${ImageWrapper} {
          margin: 0;
          max-width: 100%;
          width: min-content;
          flex-basis: 60%;
        }
        ${ContentWrapper} {
          flex-basis: 40%;
          padding: 0 36px;
          max-width: 433px;
        }

        ${({ $isEven }) => {
          switch ($isEven) {
            case false:
              return css`
                justify-content: flex-end;
                ${ImageWrapper} {
                  justify-content: flex-start;
                }
              `;
            case true:
            default:
              return css`
                justify-content: flex-end;
                ${ImageWrapper} {
                  justify-content: flex-end;
                }
              `;
          }
        }}
      `
    )}

    ${({ $isEven }) => {
      switch ($isEven) {
        case false:
          return css`
            flex-direction: row-reverse;
            > * + * {
              ${fluidProp(['margin-right'], '20px', '60px')}
            }
          `;
        case true:
        default:
          return css`
            flex-direction: row;
            > * + * {
              ${fluidProp(['margin-left'], '20px', '60px')}
            }
          `;
      }
    }}
  `)}
`;

export const ImageOuterWrapper = styled.div`
  ${fromTabletBreakpoint(css`
    position: absolute;
    max-width: min(50%, 800px);
    top: 50%;
    transform: translateY(-50%);

    ${({ $isEven }) => {
      switch ($isEven) {
        case false:
          return css`
            left: 0;
          `;
        case true:
        default:
          return css`
            right: 0;
          `;
      }
    }}

    ${({ $withFAQ }) => {
      switch ($withFAQ) {
        case true:
          return css`
            top: 300px;
          `;
        case false:
        default:
          return css`
            top: 50%;
          `;
      }
    }}
  `)}
`;

export const SectionBubbleLabel = styled(ComingSoonWrapper)`
  display: inline-block;
  margin-bottom: 16px;
`;

export const SectionLabel = styled.p`
  ${({ theme }) => theme.typography.bodyCaption}
  font-weight: 600;

  text-transform: uppercase;
  margin-bottom: 16px;

  ${({ $textTone }) => {
    switch ($textTone) {
      case 'light':
        return css`
          color: var(--colors-turquoise);
        `;
      default:
        return css`
          color: var(--colors-irisBlue);
        `;
    }
  }}
`;

export const SectionTitleComponent = styled.div`
  ${({ theme }) => theme.typography.titleXL}
  ${fluidProp(['font-size'], '24px', '28px')}
  font-weight: 600;
  color: var(--colors-darkBlue);
  margin-bottom: 12px;

  h2 {
    ${fluidProp('font-size', '32px', '36px')}
  }

  > * {
    font-size: inherit;
    font-weight: inherit;
    margin: inherit;
  }
`;

export const OverviewSection = styled.section`
  color: var(--colors-bayoux);

  ${({ $tone }) => {
    switch ($tone) {
      case 'light':
        return css`
          background-image: none;
          background-color: var(--colors-blank);
        `;
      case 'purpleMinsk':
        return css`
          background-image: var(--colors-purpleMinsk);
        `;
      default:
        return css`
          background-image: linear-gradient(180deg, #f9fbff 0%, #f0f5ff 100%);
        `;
    }
  }}
`;

export const OverviewDescriptionComponent = styled.div`
  ${({ theme }) => theme.typography.bodyLead}
  line-height: 1.6
`;

export const SectionCustomTitleComponent = styled(SectionTitleComponent)`
  && {
    ${({ $textTone }) => {
      switch ($textTone) {
        case 'light':
          return css`
            color: var(--colors-blank);
          `;
        default:
          return css`
            color: var(--colors-darkBlue);
          `;
      }
    }}
  }
`;

export const SectionCustomDescriptionComponent = styled(OverviewDescriptionComponent)`
  ${({ $textTone }) => {
    switch ($textTone) {
      case 'light':
        return css`
          color: var(--colors-moonRaker);
        `;
      default:
        return css`
          color: var(--colors-bayoux);
        `;
    }
  }}
`;

export const Actions = styled.div`
  margin-top: 25px;

  > * {
    margin-top: 15px;
  }
`;

// TODO: add option for the right side icons
const BackgroundIconWrapper = styled.div`
  position: absolute;
  left: 0;
  top: -100px;
  z-index: -1;

  ${tabletBreakpoint(css`
    display: none;
  `)}
`;

const BackgroundIcon = styled(Image)``;

const LabelIconWrapper = styled.div`
  padding-bottom: 15px;
`;

const TwoColumnDescriptionsWrapper = styled.div`
  display: grid;
  gap: 16px;
  grid-template-columns: 1fr;

  ${whenProp(
    '$bothColumnsHaveContent',
    css`
      grid-template-columns: 1fr 1fr;
    `
  )}

  ${mobileBreakpoint(css`
    grid-template-columns: 1fr;
  `)}
`;

const CustomLinkButton = styled(LinkButton)`
  box-sizing: border-box;
  height: 45px;
  margin-right: 7px;
`;

export function OverviewSectionRow({
  label_icon: labelIcon,
  illustration,
  bubble_label: bubbleLabel,
  label,
  title,
  description,
  two_column_description_left: twoColumnDescriptionLeft,
  two_column_description_right: twoColumnDescriptionRight,
  has_call_to_action: hasCTA,
  button_label: buttonLabel,
  button_has_arrow: buttonHasArrow,
  button_is_inline: buttonIsInline,
  button_link: buttonLink,
  illustration_on_the_edge: onEdge,
  wideContent = false,
  index,
  CTAButton,
  faq,
  question,
  answer,
  cta_text: ctaText,
  cta_link: ctaLink,
  tone,
  icon,
  directionOffset,
  background_icon: backgroundIcon,
  background_icon_name: backgroundIconName,
  second_button_label: secondButtonLabel,
  second_button_link: secondButtonLink,
  bigImage,
  cta_button_label: ctaButtonLabel,
  cta_hubspot_form_id: ctaHubspotFormId,
  cta_form_title: ctaFormTitle,
  cta_form_description: ctaFormDescription,
  cta_form_event_name: ctaFormEventName,
  section_link_id: sectionLinkId,
}) {
  const isEven = directionOffset ? index % 2 !== 0 : index % 2 === 0;

  return (
    <AnimatedSection
      ComponentName={Wrapper}
      $wideContent={wideContent}
      $onEdge={illustration && onEdge}
    >
      <SectionContentContainer
        $isEven={isEven}
        $wideContent={wideContent}
        $onEdge={onEdge}
        $withFAQ={faq}
        {...(sectionLinkId ? { id: sectionLinkId } : {})}
      >
        {backgroundIcon && backgroundIconName === 'heart' && (
          <BackgroundIconWrapper>
            <BackgroundIcon src={Heart} alt="" />
          </BackgroundIconWrapper>
        )}
        {backgroundIcon && backgroundIconName === 'users' && (
          <BackgroundIconWrapper>
            <BackgroundIcon src={Users} alt="" />
          </BackgroundIconWrapper>
        )}
        <ContentWrapper>
          {labelIcon && (
            <LabelIconWrapper>
              <Image
                src={labelIcon.url}
                alt={labelIcon.alt || ''}
                width={labelIcon.dimensions.width ?? 55}
                height={labelIcon.dimensions.height ?? 55}
                quality="100"
              />
            </LabelIconWrapper>
          )}
          {bubbleLabel && <SectionBubbleLabel>{bubbleLabel}</SectionBubbleLabel>}
          {label && <SectionLabel>{label}</SectionLabel>}
          {fieldHasContent(title) && (
            <SectionTitleComponent>
              <PrismicRichText field={title} />
            </SectionTitleComponent>
          )}
          {fieldHasContent(description) && (
            <OverviewDescriptionComponent>
              <PrismicRichText field={description} components={defaultPrismicComponents} />
            </OverviewDescriptionComponent>
          )}
          {(fieldHasContent(twoColumnDescriptionLeft) ||
            fieldHasContent(twoColumnDescriptionRight)) && (
            <TwoColumnDescriptionsWrapper
              $bothColumnsHaveContent={
                fieldHasContent(twoColumnDescriptionLeft) &&
                fieldHasContent(twoColumnDescriptionRight)
              }
            >
              {fieldHasContent(twoColumnDescriptionLeft) && (
                <OverviewDescriptionComponent>
                  <PrismicRichText
                    field={twoColumnDescriptionLeft}
                    components={defaultPrismicComponents}
                  />
                </OverviewDescriptionComponent>
              )}
              {fieldHasContent(twoColumnDescriptionRight) && (
                <OverviewDescriptionComponent>
                  <PrismicRichText
                    field={twoColumnDescriptionRight}
                    components={defaultPrismicComponents}
                  />
                </OverviewDescriptionComponent>
              )}
            </TwoColumnDescriptionsWrapper>
          )}

          {faq && (
            <QuestionsAndConsiderations
              question={question}
              answer={answer}
              ctaText={ctaText}
              ctaLink={ctaLink}
              tone={tone}
              icon={icon}
            />
          )}
          {(hasCTA || buttonLabel || ctaButtonLabel) && (
            <Actions>
              {buttonLabel &&
                (CTAButton ? (
                  <CTAButton>{buttonLabel ?? "Let's talk"}</CTAButton>
                ) : (
                  <CustomLinkButton
                    withBackground={!buttonIsInline}
                    irisBlueBG={!buttonIsInline}
                    label={buttonLabel ?? "Let's talk"}
                    chevron={(buttonHasArrow && !buttonIsInline) ?? true}
                    arrow={buttonHasArrow && buttonIsInline}
                    {...(buttonLink
                      ? { href: buttonLink?.url, target: buttonLink?.target }
                      : defaultCTAProps)}
                  />
                ))}
              {secondButtonLabel && (
                <CustomLinkButton
                  withBackground={!buttonIsInline}
                  withBorder={!buttonIsInline}
                  borderWidth="2px"
                  label={secondButtonLabel ?? 'Learn more'}
                  borderColor="irisBlue"
                  chevron={(buttonHasArrow && !buttonIsInline) ?? true}
                  arrow={buttonHasArrow && buttonIsInline}
                  {...(buttonLink
                    ? { href: secondButtonLink?.url, target: secondButtonLink?.target }
                    : defaultCTAProps)}
                />
              )}
              {ctaButtonLabel && (
                <OverviewSectionFormButton
                  formId={ctaHubspotFormId}
                  formTitle={ctaFormTitle}
                  formDescription={ctaFormDescription}
                  formEventName={ctaFormEventName}
                >
                  {ctaButtonLabel}
                </OverviewSectionFormButton>
              )}
            </Actions>
          )}
          {bigImage && (
            <BigImageContainer $onEdge={onEdge}>
              <BigImageWrapper $isEven={isEven}>
                {illustration && !onEdge && (
                  <Image
                    src={illustration.url}
                    alt={illustration.alt || ''}
                    width={illustration.dimensions.width}
                    height={illustration.dimensions.height}
                    quality="100"
                  />
                )}
              </BigImageWrapper>
            </BigImageContainer>
          )}
        </ContentWrapper>

        <ImageWrapper $bigImage={bigImage}>
          {illustration && !onEdge && (
            <Image
              src={illustration.url}
              alt={illustration.alt || ''}
              width={illustration.dimensions.width}
              height={illustration.dimensions.height}
              quality="100"
            />
          )}
        </ImageWrapper>
      </SectionContentContainer>
      {illustration && onEdge && (
        <ImageOuterWrapper $isEven={isEven} $withFAQ={faq}>
          <Image
            src={illustration.url}
            alt={illustration.alt || ''}
            width={illustration.dimensions.width}
            height={illustration.dimensions.height}
            quality="100"
          />
        </ImageOuterWrapper>
      )}
    </AnimatedSection>
  );
}

const NoteWrapper = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 10px;
  margin-top: 45px;

  ${({ $bgColor }) => {
    switch ($bgColor) {
      case true: {
        return css`
          background: var(--colors-blank);
        `;
      }
      case false:
      default:
        return css`
          background: ${({ theme }) => theme.colors.zircon};
        `;
    }
  }}
`;

const NoteIconWrapper = styled.div`
  background: var(--colors-blank);
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  left: -6.5%;
  width: 54px;
  height: 54px;
  border-radius: 50%;

  ${tabletBreakpoint(css`
    left: -3.5%;
  `)}

  ${({ $bgColor }) => {
    switch ($bgColor) {
      case true: {
        return css`
          background: var(--colors-blank);
        `;
      }
      case false:
      default:
        return css`
          background: ${({ theme }) => theme.colors.zircon};
        `;
    }
  }}
`;

const NoteComponent = styled.div`
  ${({ theme }) => theme.typography.bodyLeadBold}
  font-weight: 600;
  color: var(--colors-darkBlue);
  padding: 26px 23px 22px;

  ${desktopBreakpoint(css`
    padding-left: 30px;
  `)}

  ${tabletBreakpoint(css`
    padding-left: 40px;
  `)}

    ${mobileBreakpoint(css`
    padding-left: 45px;
  `)}

  > * {
    font-size: inherit;
    font-weight: inherit;
    margin: inherit;
  }
`;

function SectionNote({ note, icon, bgColor }) {
  return (
    <>
      <NoteWrapper $bgColor={bgColor}>
        <NoteIconWrapper $bgColor={bgColor}>
          <Image
            src={icon.url}
            alt={icon.alt || ''}
            width={icon.dimensions.width}
            height={icon.dimensions.height}
            quality="100"
          />
        </NoteIconWrapper>
        <NoteComponent>
          <PrismicRichText field={note} components={defaultPrismicComponents} />
        </NoteComponent>
      </NoteWrapper>
    </>
  );
}

export function OverviewSectionSingleRow({
  illustration,
  label,
  title,
  description,
  button_label: buttonLabel,
  button_link: buttonLink,
  illustration_on_the_edge: onEdge,
  wideContent = false,
  index,
  CTAButton,
  faq,
  question,
  answer,
  cta_text: ctaText,
  cta_link: ctaLink,
  tone,
  icon,
  directionOffset,
  textTone,
  noChevron,
  note,
  bgColor,
}) {
  const isEven = directionOffset ? index % 2 !== 0 : index % 2 === 0;
  return (
    <AnimatedSection
      ComponentName={WrapperSingleBlock}
      $wideContent={wideContent}
      $onEdge={illustration && onEdge}
    >
      <SectionContentContainer $isEven={isEven} $wideContent={wideContent} $withFAQ={faq}>
        <ContentWrapper>
          {label && <SectionLabel $textTone={textTone}>{label}</SectionLabel>}
          {fieldHasContent(title) && (
            <SectionCustomTitleComponent $textTone={textTone}>
              <PrismicRichText field={title} />
            </SectionCustomTitleComponent>
          )}
          {fieldHasContent(description) && (
            <SectionCustomDescriptionComponent $textTone={textTone}>
              <PrismicRichText field={description} components={defaultPrismicComponents} />
            </SectionCustomDescriptionComponent>
          )}
          {faq && (
            <QuestionsAndConsiderations
              question={question}
              answer={answer}
              ctaText={ctaText}
              ctaLink={ctaLink}
              tone={tone}
              icon={icon}
            />
          )}
          {icon && fieldHasContent(note) && (
            <SectionNote note={note} icon={icon} bgColor={bgColor} />
          )}
          {buttonLabel && (
            <Actions>
              {CTAButton ? (
                <CTAButton>{buttonLabel ?? "Let's talk"}</CTAButton>
              ) : (
                <LinkButton
                  withBackground
                  irisBlueBG
                  chevron={!noChevron}
                  label={buttonLabel ?? "Let's talk"}
                  href={buttonLink?.url ?? `${process.env.NEXT_PUBLIC_EMPLOY_BASE_URL}/sign-up`}
                />
              )}
            </Actions>
          )}
        </ContentWrapper>
        <ImageWrapper>
          {illustration && !onEdge && (
            <Image
              src={illustration.url}
              alt={illustration.alt || ''}
              width={illustration.dimensions.width}
              height={illustration.dimensions.height}
              quality="100"
            />
          )}
        </ImageWrapper>
      </SectionContentContainer>
      {illustration && onEdge && (
        <ImageOuterWrapper $isEven={isEven} $withFAQ={faq}>
          <Image
            src={illustration.url}
            alt={illustration.alt || ''}
            width={illustration.dimensions.width}
            height={illustration.dimensions.height}
            quality="100"
          />
        </ImageOuterWrapper>
      )}
    </AnimatedSection>
  );
}
